import axios from 'axios';

import coldemailer_config from './coldemailer_config';

// Builds the query string, always including the user id the host app's guard expects
const build_query = (params) => {
	const parts = [];
	const user_id = coldemailer_config.get_user_id();

	if(user_id) {
		parts.push('user_id=' + encodeURIComponent(user_id));
	}

	const names = params ? Object.keys(params) : [];
	for(var i=0; i < names.length; ++i) {
		const value = params[names[i]];
		if(value !== null && value !== undefined && value !== '') {
			parts.push(encodeURIComponent(names[i]) + '=' + encodeURIComponent(value));
		}
	}

	if(!parts.length) {
		return '';
	}

	return '?' + parts.join('&');
};

const build_headers = () => {
	const token = coldemailer_config.get_auth_token();
	if(!token) {
		return {};
	}

	return { Authorization: 'Bearer ' + token };
};

const build_url = (path, params) => {
	return coldemailer_config.server_url + coldemailer_config.api_prefix + path + build_query(params);
};

const coldemailer_api = {

	get_email_campaigns: async () => {
		const response = await axios.get(build_url('/email_campaigns'), { headers: build_headers() });
		return response.data.email_campaigns ? response.data.email_campaigns : [];
	},

	get_email_campaign: async (id) => {
		const response = await axios.get(build_url('/email_campaigns/' + id), { headers: build_headers() });
		return response.data.email_campaign;
	},

	create_email_campaign: async (campaign_data) => {
		const response = await axios.post(build_url('/email_campaigns'), campaign_data, { headers: build_headers() });
		return response.data.email_campaign;
	},

	update_email_campaign: async (id, campaign_data) => {
		const response = await axios.put(build_url('/email_campaigns/' + id), campaign_data, { headers: build_headers() });
		return response.data.email_campaign;
	},

	archive_email_campaign: async (id) => {
		const response = await axios.delete(build_url('/email_campaigns/' + id), { headers: build_headers() });
		return response.data;
	},

	get_email_senders: async () => {
		const response = await axios.get(build_url('/email_senders'), { headers: build_headers() });
		return response.data.email_senders ? response.data.email_senders : [];
	},

	get_campaign_emails: async (email_campaign_id) => {
		const response = await axios.get(build_url('/campaign_emails', { email_campaign_id }), { headers: build_headers() });
		return response.data.campaign_emails ? response.data.campaign_emails : [];
	},

	// Asks the backend to author a sequence with Claude. Nothing is saved until create_campaign_email runs.
	generate_campaign_emails: async (brief) => {
		const response = await axios.post(build_url('/generate_campaign_emails'), brief, { headers: build_headers() });
		return response.data.campaign_emails ? response.data.campaign_emails : [];
	},

	create_campaign_email: async (campaign_email_data) => {
		const response = await axios.post(build_url('/campaign_emails'), campaign_email_data, { headers: build_headers() });
		return response.data.campaign_email;
	},

	get_campaign_email: async (id) => {
		const response = await axios.get(build_url('/campaign_emails/' + id), { headers: build_headers() });
		return response.data.campaign_email;
	},

	update_campaign_email: async (id, campaign_email_data) => {
		const response = await axios.put(build_url('/campaign_emails/' + id), campaign_email_data, { headers: build_headers() });
		return response.data.campaign_email;
	},

	delete_campaign_email: async (id) => {
		const response = await axios.delete(build_url('/campaign_emails/' + id), { headers: build_headers() });
		return response.data;
	},

	// Whether the service is running time compressed. Drives the test mode banner.
	get_test_status: async () => {
		const response = await axios.get(build_url('/test_status'), { headers: build_headers() });
		return response.data;
	},

	// Switches the service between Test and Live, and sets the compression factor used in Test
	set_test_status: async (system_status, time_compression_factor) => {
		const response = await axios.put(build_url('/test_status'), { system_status, time_compression_factor }, { headers: build_headers() });
		return response.data;
	},

	// Turns sending on or off. The service holds an outer gate in code that must also allow it.
	set_enable_sending: async (enable_sending) => {
		const response = await axios.put(build_url('/enable_sending'), { enable_sending }, { headers: build_headers() });
		return response.data;
	},

	// Returns the given addresses to a clean state. Only available while in test mode.
	test_reset_recipients: async (emails) => {
		const response = await axios.post(build_url('/test_reset_recipients'), { emails }, { headers: build_headers() });
		return response.data;
	},

	// Paged server side, so the caller passes page/per_page and gets the total back for the controls
	get_outreach_recipients: async (params) => {
		const response = await axios.get(build_url('/outreach_recipients', params), { headers: build_headers() });

		return {
			outreach_recipients: response.data.outreach_recipients ? response.data.outreach_recipients : [],
			total: response.data.total ? response.data.total : 0
		};
	},

	// Returns the recipient plus its content, scheduled emails, list memberships and suppression state
	get_outreach_recipient: async (id) => {
		const response = await axios.get(build_url('/outreach_recipients/' + id), { headers: build_headers() });
		return response.data;
	},

	update_outreach_recipient: async (id, recipient_data) => {
		const response = await axios.put(build_url('/outreach_recipients/' + id), recipient_data, { headers: build_headers() });
		return response.data.outreach_recipient;
	},

	// Cancels pending emails, adds the address to the suppression list and marks the recipient suppressed
	suppress_outreach_recipient: async (id) => {
		const response = await axios.delete(build_url('/outreach_recipients/' + id), { headers: build_headers() });
		return response.data;
	},

	// How many addresses are waiting on the verification provider right now
	get_verification_progress: async () => {
		const response = await axios.get(build_url('/verification_progress'), { headers: build_headers() });
		return response.data;
	},

	// Removes the recipients outright, along with anything prepared or scheduled for them. The
	// addresses are not suppressed, so they can be queued again later.
	delete_outreach_recipients: async (recipient_ids) => {
		const response = await axios.post(build_url('/delete_outreach_recipients'), { recipient_ids }, { headers: build_headers() });
		return response.data;
	},

	// Read only browse over the LeadDiscovery Company table. Paging and every exclusion filter
	// are applied by the server, so the caller passes them and uses the total that comes back.
	get_companies: async (params) => {
		const response = await axios.get(build_url('/companies', params), { headers: build_headers() });

		return {
			rows: response.data.rows ? response.data.rows : [],
			total: response.data.total ? response.data.total : 0
		};
	},

	// Reads every company matching the filters and adds them to a named list. The backend does the
	// paging and sanitising, so this is one request however many companies match.
	add_companies_to_list: async (request_data) => {
		const response = await axios.post(build_url('/add_companies_to_list'), request_data, { headers: build_headers() });
		return response.data;
	},

	// One company with every column, for the detail view
	get_company: async (id) => {
		const response = await axios.get(build_url('/companies/' + id), { headers: build_headers() });
		return response.data.company;
	},

	// The values each status column holds, so the browse view's dropdowns are built from the data
	get_company_status_values: async () => {
		const response = await axios.get(build_url('/company_status_values'), { headers: build_headers() });
		return response.data.status_values ? response.data.status_values : {};
	},

	get_email_lists: async () => {
		const response = await axios.get(build_url('/email_lists'), { headers: build_headers() });
		return response.data.email_lists ? response.data.email_lists : [];
	},

	get_email_list: async (id) => {
		const response = await axios.get(build_url('/email_lists/' + id), { headers: build_headers() });
		return response.data.email_list;
	},

	// Permanently removes the list and every company on it. The archive_email_list call above only
	// sets the status, leaving the row and its members in place.
	delete_email_list: async (id) => {
		const response = await axios.delete(build_url('/email_list_permanent_delete/' + id), { headers: build_headers() });
		return response.data;
	},

	// The companies on a list. Separate from the list's recipients, which are the send side.
	get_email_list_members: async (id) => {
		const response = await axios.get(build_url('/email_list_members/' + id), { headers: build_headers() });
		return response.data.members ? response.data.members : [];
	},

	// Turns the given companies on a list into recipients the send path can work through
	add_members_as_recipients: async (id, member_ids) => {
		const response = await axios.post(build_url('/email_list_add_recipients/' + id), { member_ids }, { headers: build_headers() });
		return response.data;
	},

	// One touch built the way a send would build it, without sending or storing anything
	preview_email: async (email_campaign_id, recipient_id, touch_number) => {
		const response = await axios.get(build_url('/preview_email', { email_campaign_id, recipient_id, touch_number }), { headers: build_headers() });
		return response.data;
	}
};

export default coldemailer_api;
